<template>
  <div class="telegram-ai-allowed-users-select">
    <label v-if="label" class="form-label" :for="inputId">{{ label }}</label>
    <div v-if="loading" class="text-muted small mb-2">
      <i class="fas fa-spinner fa-spin me-1" aria-hidden="true"></i>
      {{ $t("Loading") }}…
    </div>
    <multiselect
      v-else
      :id="inputId"
      :value="value"
      :options="options"
      :multiple="true"
      :close-on-select="false"
      :clear-on-select="true"
      :preserve-search="true"
      :preselect-first="false"
      :searchable="true"
      :show-labels="true"
      :disabled="disabled"
      label="name"
      track-by="_id"
      :placeholder="placeholder || $t('telegramAi.allowedUsersPlaceholder')"
      @input="onSelectionChange"
    >
      <template #option="props">
        <div>
          <span>{{ props.option.name }}</span>
          <small class="text-muted ms-2">({{ props.option.email }})</small>
          <small
            v-if="props.option.telegramUsername"
            class="text-primary ms-1"
          >@{{ props.option.telegramUsername }}</small>
          <small v-else class="text-warning ms-1">— {{ $t("telegramAi.noTelegramOnProfile") }}</small>
        </div>
      </template>
      <template #tag="props">
        <span
          class="multiselect__tag"
          :class="{ 'border border-warning': !hasTelegramUsername(props.option) }"
        >
          {{ tagLabel(props.option) }}
          <i
            class="multiselect__tag-icon"
            aria-hidden="true"
            tabindex="1"
            @click="props.remove(props.option)"
          ></i>
        </span>
      </template>
    </multiselect>
    <small v-if="hint" class="text-muted d-block mt-1">{{ hint }}</small>
    <p v-if="withoutTelegramWarning.length" class="small text-warning mb-0 mt-1">
      {{ $t("telegramAi.allowlistMissingTelegramHint") }}
      <span class="d-block mt-1">
        <template v-for="(u, idx) in withoutTelegramWarning">
          <span v-if="idx > 0"> · </span>{{ u.name }}
        </template>
      </span>
    </p>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import Swal from "sweetalert2";
import {
  normalizeTelegramUsernameForAllowlist,
  validateTelegramAllowlistSelection,
} from "@/modules/telegram_ai_module/utils/telegramAllowlistSelection";

export default {
  name: "TelegramAiAllowedUsersSelect",
  components: { Multiselect },
  props: {
    value: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    label: { type: String, default: "" },
    hint: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    inputId: { type: String, default: "tgAiAllowedUsers" },
  },
  data() {
    return {
      withoutTelegramWarning: [],
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(users) {
        const check = validateTelegramAllowlistSelection(users);
        this.withoutTelegramWarning = check.withoutTelegram;
      },
    },
  },
  methods: {
    hasTelegramUsername(option) {
      return !!normalizeTelegramUsernameForAllowlist(
        option && option.telegramUsername
      );
    },
    tagLabel(option) {
      const name = (option && option.name) || "";
      const uname = normalizeTelegramUsernameForAllowlist(
        option && option.telegramUsername
      );
      if (uname) return `${name} (@${uname})`;
      return `${name} — ${this.$t("telegramAi.noTelegramOnProfile")}`;
    },
    onSelectionChange(next) {
      const selected = Array.isArray(next) ? next : [];
      const check = validateTelegramAllowlistSelection(selected);
      if (!check.ok) {
        const labels = check.duplicateUsernames.map((u) => `@${u}`).join(", ");
        Swal.fire({
          icon: "warning",
          title: this.$tn("telegramAi.allowlistDuplicateTitle"),
          text: this.$tn("telegramAi.allowlistDuplicateText", { usernames: labels }),
          confirmButtonText: this.$tn("OK") || "OK",
        });
        return;
      }
      this.withoutTelegramWarning = check.withoutTelegram;
      this.$emit("input", selected);
      this.$emit("selection-validated", {
        withoutTelegram: check.withoutTelegram,
      });
    },
  },
};
</script>
